import React, { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { KeyRound, Download, LogOut, Loader2, FileSpreadsheet, ShieldCheck, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAGE_KEY = 'kp_partner_api_key';

export default function PartnerPortal() {
  // Allow ?key=kpb_... in URL for direct access
  const urlKey = (() => {
    try { return new URLSearchParams(window.location.search).get('key') || ''; } catch { return ''; }
  })();

  const [apiKey, setApiKey] = useState(urlKey || localStorage.getItem(STORAGE_KEY) || '');
  const [authed, setAuthed] = useState(false);
  const [checking, setChecking] = useState(false);
  const [info, setInfo] = useState(null); // { total }
  const [downloading, setDownloading] = useState(false);
  const [filters, setFilters] = useState({ country: '', language: '', dr_min: '', dr_max: '' });

  // Auto-validate if a key is already present
  useEffect(() => {
    if (apiKey && !authed && !checking) {
      handleLogin(apiKey);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLogin = async (key) => {
    const k = (key || apiKey).trim();
    if (!k.startsWith('kpb_')) {
      toast.error('La clave debe empezar por kpb_');
      return;
    }
    setChecking(true);
    try {
      const r = await fetch(`${API}/external/products?limit=1`, {
        headers: { Authorization: `Bearer ${k}` },
      });
      if (r.status === 401) {
        toast.error('Clave inválida o revocada');
        setAuthed(false);
        return;
      }
      if (!r.ok) {
        toast.error(`Error ${r.status}`);
        return;
      }
      const data = await r.json();
      setInfo({ total: data.total || 0 });
      setAuthed(true);
      localStorage.setItem(STORAGE_KEY, k);
      setApiKey(k);
    } catch (e) {
      toast.error('No se pudo conectar con el servidor');
    } finally {
      setChecking(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem(STORAGE_KEY);
    setApiKey('');
    setAuthed(false);
    setInfo(null);
  };

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const params = new URLSearchParams();
      if (filters.country.trim()) params.set('country', filters.country.trim());
      if (filters.language.trim()) params.set('language', filters.language.trim());
      if (filters.dr_min) params.set('dr_min', filters.dr_min);
      if (filters.dr_max) params.set('dr_max', filters.dr_max);

      const url = `${API}/external/products.xlsx${params.toString() ? '?' + params.toString() : ''}`;
      const r = await fetch(url, { headers: { Authorization: `Bearer ${apiKey}` } });
      if (!r.ok) {
        toast.error(`Error al descargar: ${r.status}`);
        return;
      }
      const blob = await r.blob();
      const dlUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = dlUrl;
      const today = new Date().toISOString().slice(0, 10);
      a.download = `catalogo_${today}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(dlUrl);
      toast.success('Excel descargado');
    } catch (e) {
      toast.error('Error al descargar el Excel');
    } finally {
      setDownloading(false);
    }
  };

  // ============================================================
  // LOGIN VIEW
  // ============================================================
  if (!authed) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-indigo-950 to-slate-900 flex items-center justify-center p-6">
        <Card className="w-full max-w-md shadow-2xl border-slate-700 bg-white">
          <CardHeader className="text-center space-y-2">
            <div className="mx-auto bg-indigo-100 rounded-full p-3 w-fit">
              <KeyRound className="h-7 w-7 text-indigo-600" />
            </div>
            <CardTitle className="text-2xl">Portal Partner KeyPubli</CardTitle>
            <CardDescription>
              Introduce la clave que te ha facilitado tu contacto en KeyPubli para acceder al catálogo y descargarlo en Excel.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="apikey" className="text-sm font-medium">Clave de acceso</Label>
              <Input
                id="apikey"
                type="password"
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleLogin()}
                placeholder="kpb_xxxxxxxxxxxxxxxxxxx"
                className="font-mono"
                data-testid="partner-login-key"
                autoFocus
              />
              <p className="text-xs text-muted-foreground mt-1">
                Empieza por <code className="bg-slate-100 px-1 rounded">kpb_</code> · Si no tienes una, contacta con KeyPubli.
              </p>
            </div>
            <Button
              onClick={() => handleLogin()}
              disabled={checking || !apiKey}
              className="w-full"
              data-testid="partner-login-btn"
            >
              {checking ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Comprobando…</>
              ) : (
                <><ShieldCheck className="h-4 w-4 mr-2" /> Entrar</>
              )}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // ============================================================
  // AUTHED VIEW
  // ============================================================
  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="bg-indigo-100 rounded-full p-2">
              <ShieldCheck className="h-5 w-5 text-indigo-600" />
            </div>
            <div>
              <div className="font-semibold text-slate-800">Portal Partner KeyPubli</div>
              <div className="text-xs text-muted-foreground font-mono">{apiKey.slice(0, 12)}…</div>
            </div>
          </div>
          <Button variant="ghost" size="sm" onClick={handleLogout} data-testid="partner-logout-btn">
            <LogOut className="h-4 w-4 mr-1" /> Salir
          </Button>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-8 space-y-6">
        {/* Stats */}
        <Card>
          <CardHeader className="pb-3">
            <CardDescription>Catálogo disponible</CardDescription>
            <CardTitle className="text-3xl">
              {info?.total?.toLocaleString('es-ES') || 0} <span className="text-base font-normal text-muted-foreground">productos</span>
            </CardTitle>
          </CardHeader>
        </Card>

        {/* Download card */}
        <Card className="border-emerald-200 bg-emerald-50/40">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <FileSpreadsheet className="h-5 w-5 text-emerald-600" />
              Descargar catálogo en Excel
            </CardTitle>
            <CardDescription>
              Aplica filtros opcionales y descarga el catálogo completo como archivo <code className="bg-white px-1 rounded">.xlsx</code>. Déjalos vacíos para incluir todos los productos.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
              <div>
                <Label className="text-xs">País (contiene)</Label>
                <Input
                  value={filters.country}
                  onChange={(e) => setFilters({ ...filters, country: e.target.value })}
                  placeholder="Spain"
                  data-testid="filter-country"
                />
              </div>
              <div>
                <Label className="text-xs">Idioma</Label>
                <Select
                  value={filters.language || 'all'}
                  onValueChange={(v) => setFilters({ ...filters, language: v === 'all' ? '' : v })}
                >
                  <SelectTrigger data-testid="filter-language"><SelectValue placeholder="Todos" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos</SelectItem>
                    <SelectItem value="ES">ES</SelectItem>
                    <SelectItem value="EN">EN</SelectItem>
                    <SelectItem value="DE">DE</SelectItem>
                    <SelectItem value="FR">FR</SelectItem>
                    <SelectItem value="IT">IT</SelectItem>
                    <SelectItem value="PT">PT</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">DR mínimo</Label>
                <Input
                  type="number"
                  min="0" max="100"
                  value={filters.dr_min}
                  onChange={(e) => setFilters({ ...filters, dr_min: e.target.value })}
                  placeholder="30"
                  data-testid="filter-dr-min"
                />
              </div>
              <div>
                <Label className="text-xs">DR máximo</Label>
                <Input
                  type="number"
                  min="0" max="100"
                  value={filters.dr_max}
                  onChange={(e) => setFilters({ ...filters, dr_max: e.target.value })}
                  placeholder="100"
                  data-testid="filter-dr-max"
                />
              </div>
            </div>

            <Button
              onClick={handleDownload}
              disabled={downloading}
              size="lg"
              className="w-full bg-emerald-600 hover:bg-emerald-700"
              data-testid="download-excel-btn"
            >
              {downloading ? (
                <><Loader2 className="h-5 w-5 mr-2 animate-spin" /> Generando Excel…</>
              ) : (
                <><Download className="h-5 w-5 mr-2" /> Descargar Excel</>
              )}
            </Button>

            <div className="text-xs text-muted-foreground bg-white p-3 rounded border border-emerald-100 flex gap-2">
              <AlertTriangle className="h-4 w-4 text-amber-500 flex-shrink-0" />
              <span>
                Los precios <b>PC</b>, <b>PVP</b> y <b>Premium</b> son confidenciales. No los compartas fuera de tu equipo.
              </span>
            </div>
          </CardContent>
        </Card>

        {/* Direct URL helper */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Acceso directo con un enlace</CardTitle>
            <CardDescription>
              Puedes guardar este enlace en favoritos para entrar sin volver a teclear la clave (úsalo solo en equipos privados):
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex gap-2 items-center">
              <Input
                readOnly
                value={`${window.location.origin}/partner?key=${apiKey}`}
                className="font-mono text-xs"
                onClick={(e) => e.target.select()}
              />
              <Button
                variant="outline"
                onClick={() => {
                  navigator.clipboard.writeText(`${window.location.origin}/partner?key=${apiKey}`);
                  toast.success('Enlace copiado');
                }}
              >
                Copiar
              </Button>
            </div>
          </CardContent>
        </Card>

        <p className="text-center text-xs text-muted-foreground pt-4">
          ¿Problemas? Contacta con tu responsable en KeyPubli y comprueba que tu clave esté activa.
        </p>
      </main>
    </div>
  );
}
